import { Injectable } from '@angular/core';
import { MatDrawerMode, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { LayoutSettings } from './edit/predefined-templates.service';
import { StorageService } from './storage/storage.service';
import { SharedService } from './shared/shared.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

export interface SideNavConfig {
  mode?: MatDrawerMode;
  position: 'end' | 'start';
  template: string;
  data?: any;
}

export interface ScreenRange {
  gtXs: boolean;
  gtSm: boolean;
  gtMd: boolean;
  gtLg: boolean;
  ltSm: boolean;
  ltMd: boolean;
  ltLg: boolean;
  ltXl: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  readonly screenSizeMap = new Map([
    [Breakpoints.XSmall, 'xs'],
    [Breakpoints.Small, 'sm'],
    [Breakpoints.Medium, 'md'],
    [Breakpoints.Large, 'lg'],
    [Breakpoints.XLarge, 'xl'],
  ]);
  readonly SIDENAV_MODE = 'over';
  readonly SIDENAV_POSITION = 'end';
  private _sideNav: MatSidenav;
  private _sideNavConfig = new BehaviorSubject<SideNavConfig>(null);

  private _contentComponent: MatSidenavContent;
  private _contentComponentLoaded = new BehaviorSubject<boolean>(false);

  private _layoutSettings = new BehaviorSubject<LayoutSettings>(null);

  private _headerHeight = new BehaviorSubject<number>(0);
  private _headerFooterHeight = new BehaviorSubject<number>(900);
  private _footerHeight = new BehaviorSubject<number>(0);
  private _contentScrollOffset = new BehaviorSubject<any>(0);

  private _staffNavigationHeight = new BehaviorSubject<number>(0);

  // Responsive
  private _screenSize = new BehaviorSubject<string>(null);
  private _screenRange = new BehaviorSubject<ScreenRange>(null);

  contentComponentLoaded$ = this._contentComponentLoaded.asObservable();
  layoutSettings$ = this._layoutSettings.asObservable();
  headerHeight$ = this._headerHeight.asObservable();
  headerFooterHeight$ = this._headerFooterHeight.asObservable();
  footerHeight$ = this._footerHeight.asObservable();

  /** Sidenav-Content scroll top offset */
  contentScrollOffset$ = this._contentScrollOffset.asObservable();
  sideNavConfig$ = this._sideNavConfig.asObservable();

  staffNavigationHeight$ = this._staffNavigationHeight.asObservable();

  breakpoint$ = this._breakpointObserver.observe([
    Breakpoints.XSmall,
    Breakpoints.Small,
    Breakpoints.Medium,
    Breakpoints.Large,
    Breakpoints.XLarge,
  ]);
  screenSize$ = this._screenSize.asObservable();
  screenRange$ = this._screenRange.asObservable();

  /** Page and Drawer Layouts */
  private _pageHeaderHeight = new BehaviorSubject<number>(null);
  /** Drawer Layout */
  private _drawerOpen = new BehaviorSubject<boolean>(null);
  private _drawerMode = new BehaviorSubject<MatDrawerMode>(null);
  private _drawerWidth = new BehaviorSubject<number>(null);
  private _drawerPosition = new BehaviorSubject<'start' | 'end'>(null);
  pageHeaderHeight$ = this._pageHeaderHeight.asObservable();
  drawerOpen$ = this._drawerOpen.asObservable();
  drawerMode$ = this._drawerMode.asObservable();
  drawerWidth$ = this._drawerWidth.asObservable();
  drawerPosition$ = this._drawerPosition.asObservable();
  private _contentScrollbarWidth = new BehaviorSubject<number>(0);
  /** Width of main content scrollbars. If 0, then scrollbar is not currently shown */
  contentScrollbarWidth$ = this._contentScrollbarWidth.asObservable();

  constructor(
    private _sharedService: SharedService,
    private _storageService: StorageService,
    private _breakpointObserver: BreakpointObserver,
  ) {
    this.breakpoint$.subscribe((result) => {
      for (const query of Object.keys(result.breakpoints)) {
        if (result.breakpoints[query]) {
          const size = this.screenSizeMap.get(query);
          this._screenSize.next(size);
          this._screenRange.next({
            gtXs: size !== 'xs',
            gtSm: size !== 'xs' && size !== 'sm',
            gtMd: size !== 'xs' && size !== 'sm' && size !== 'md',
            gtLg: size !== 'xs' && size !== 'sm' && size !== 'md' && size !== 'lg',
            ltSm: size === 'xs',
            ltMd: size === 'xs' || size === 'sm',
            ltLg: size === 'xs' || size === 'sm' || size === 'md',
            ltXl: size === 'xs' || size === 'sm' || size === 'md' || size === 'lg',
          });
        }
      }
    });

    combineLatest([this.headerHeight$, this.footerHeight$]).subscribe(
      ([headerHeight, footerHeight]) => {
        this._headerFooterHeight.next(headerHeight + footerHeight);
      },
    );
  }

  setContentComponent(component: MatSidenavContent) {
    this._contentComponent = component;
    this._contentComponentLoaded.next(true);
  }

  getContentComponent(): MatSidenavContent {
    return this._contentComponent;
  }

  setContentScrollbarWidth(scrollbarWidth: number) {
    this._contentScrollbarWidth.next(scrollbarWidth);
  }

  scrollContentToTop(): void {
    this.scrollContentTo(0);
  }

  scrollContentTo(yOffset: number = 0): void {
    this._contentComponent?.scrollTo({ top: yOffset, behavior: 'smooth' });
  }

  setSideNav(sideNav: MatSidenav) {
    this._sideNav = sideNav;
  }

  openSideNav(sideNavConfig: SideNavConfig) {
    if (sideNavConfig) {
      this._sideNavConfig.next(sideNavConfig);
    }
    this._sideNav.mode = sideNavConfig?.mode ?? this.SIDENAV_MODE;
    this._sideNav.position = sideNavConfig?.position ?? this.SIDENAV_POSITION;

    this._sideNav.open();
  }

  closeSideNav() {
    this._sideNav.close();
  }

  toggleSideNav(sideNavData: SideNavConfig) {
    if (this._sideNav.opened) {
      this.closeSideNav();
    } else {
      this.openSideNav(sideNavData);
    }
  }

  setLayoutSettings(layoutSettings: LayoutSettings) {
    this._storageService.setItem('layoutSettings', layoutSettings);
    // This is stored independently so we can manipulate the header without affecting the layout settings
    this._sharedService.setLayoutSettings(layoutSettings);
    this._layoutSettings.next(layoutSettings);
  }

  setHeaderHeight(height: number) {
    if (this._headerHeight.getValue() != height) {
      this._headerHeight.next(height);
    }
  }

  setFooterHeight(height: number) {
    if (this._footerHeight.getValue() != height) {
      this._footerHeight.next(height);
    }
  }

  setContentScrollOffset(offset: number) {
    this._contentScrollOffset.next(offset);
  }

  setStaffNavigationHeight(offsetHeight: number) {
    this._staffNavigationHeight.next(offsetHeight);
  }

  // Page / Drawer Layout
  setPageHeaderHeight(height: number) {
    this._pageHeaderHeight.next(height);
  }
  // Drawer Layout
  setDrawerOpen(opened: boolean) {
    this._drawerOpen.next(opened);
  }
  setDrawerMode(mode: MatDrawerMode) {
    this._drawerMode.next(mode);
  }
  setDrawerWidth(width: number) {
    this._drawerWidth.next(width);
  }
}
